<template>
  <div>
    <div v-if="loading" class="text-center p-5">
      <b-spinner variant="info" role="status" />
    </div>
    <b-alert v-else-if="loadFailed" show variant="danger">
      {{
        record.errors.detail ||
        record.errors.options ||
        $t("message.unknownError")
      }}
      <b-button size="sm" variant="outline-danger" @click="load">{{
        $t("label.reset")
      }}</b-button>
    </b-alert>
    <b-form
      v-else
      @submit.prevent="reportUUID ? requestUpdate() : requestCreate()"
    >
      <fieldset :disabled="!record.status.save || !canEdit">
        <b-form-group :label="$t('module.user')" label-for="report-user">
          <multiselect
            id="report-user"
            v-model="createForm.user"
            :options="users"
            label="name"
            track-by="uuid"
            :placeholder="$t('label.selectX', [$t('module.user')])"
            :allow-empty="false"
            :disabled="!record.status.save || !!reportUUID"
          />
        </b-form-group>
        <div class="row">
          <div class="col-md-6">
            <b-form-group :label="$t('label.type')" label-for="report-type">
              <b-form-select
                id="report-type"
                v-model="createForm.type"
                :options="typeDdl"
                required
              />
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Platform" label-for="report-platform">
              <b-form-select
                id="report-platform"
                v-model="createForm.platformType"
                :options="platformDdl"
                required
              />
            </b-form-group>
          </div>
        </div>
        <b-form-group
          :label="$t('label.description')"
          label-for="report-description"
        >
          <b-form-textarea
            id="report-description"
            v-model="createForm.description"
            required
            maxlength="5000"
            rows="6"
          />
          <small class="text-muted"
            >{{ createForm.description.length }} / 5,000</small
          >
        </b-form-group>
        <b-alert
          :show="!!validationError || !!record.errors.save"
          variant="danger"
          >{{ validationError || record.errors.save }}</b-alert
        >
      </fieldset>
      <div class="d-flex justify-content-end border-top pt-3">
        <b-button variant="light" class="mr-2" @click="$emit('done')">{{
          $t(canEdit ? "label.cancel" : "label.close")
        }}</b-button>
        <b-button
          v-if="canEdit"
          type="submit"
          variant="info"
          :disabled="!record.status.save || !canEdit"
        >
          <b-spinner v-if="!record.status.save" small class="mr-1" />{{
            reportUUID ? $t("label.updateX", []) : $t("label.createX", [])
          }}
        </b-button>
      </div>
    </b-form>
    <div v-if="loading || loadFailed" class="text-right mt-3">
      <b-button variant="light" @click="$emit('done')">{{
        $t("label.cancel")
      }}</b-button>
    </div>
  </div>
</template>
<script>
import Multiselect from "vue-multiselect";
import { isReportEditable } from "@/models/api/report.model";
import { reportMethods } from "@/state/api";
import {
  REPORT_TYPE,
  REPORT_PLATFORM,
  reportTypeOptions,
  reportPlatformOptions,
} from "@/constants/report";

const emptyForm = () => ({
  user: null,
  type: REPORT_TYPE.OTHER,
  platformType: REPORT_PLATFORM.PORTAL,
  description: "",
});

export default {
  components: { Multiselect },
  props: {
    readOnly: { type: Boolean, default: false },
    reportUUID: { type: String, default: "" },
    searchCriteria: { type: Object, required: true },
  },
  data() {
    return {
      loading: true,
      loadFailed: false,
      validationError: "",
      createForm: emptyForm(),
    };
  },
  computed: {
    canEdit() {
      return (
        !this.readOnly &&
        (!this.reportUUID ||
          (this.record.details?.uuid === this.reportUUID &&
            isReportEditable(this.record.details)))
      );
    },
    record() {
      return this.$store.state.api.report;
    },
    typeDdl() {
      return reportTypeOptions();
    },
    platformDdl() {
      return reportPlatformOptions();
    },
    users() {
      const options = this.record.options.users || [];
      const selected = this.createForm.user;
      return selected && !options.some((user) => user.uuid === selected.uuid)
        ? [...options, selected]
        : options;
    },
  },
  watch: {
    reportUUID() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    ...reportMethods,
    async load() {
      this.loading = true;
      this.loadFailed = false;
      this.validationError = "";
      this.$store.commit("api/report/clearSaveError");
      const [options, details] = await Promise.all([
        this.readOnly ? Promise.resolve(true) : this.loadReportOptions(),
        this.reportUUID
          ? this.getReport({ uuid: this.reportUUID })
          : Promise.resolve(true),
      ]);
      this.loadFailed = !options || !details;
      if (!this.loadFailed) {
        const report = this.record.details;
        this.createForm =
          this.reportUUID && report
            ? {
                user: report.user
                  ? {
                      uuid: report.user.uuid,
                      name:
                        report.user.fullName ||
                        [report.user.firstName, report.user.lastName]
                          .filter(Boolean)
                          .join(" ") ||
                        report.user.email ||
                        "",
                    }
                  : null,
                type: report.type,
                platformType: report.platformType,
                description: report.description,
              }
            : emptyForm();
      }
      this.loading = false;
    },
    requestCreate() {
      return this.save();
    },
    requestUpdate() {
      return this.save();
    },
    async save() {
      if (!this.record.status.save || !this.canEdit) return;
      this.validationError = "";
      const description = this.createForm.description.trim();
      if (!this.createForm.user) {
        this.validationError = this.$t("message.pleaseSelectX", [
          this.$t("module.user"),
        ]);
        return;
      }
      if (!description || description.length > 5000) {
        this.validationError = this.$t("message.validX", [
          this.$t("label.description"),
        ]);
        return;
      }
      const data = {
        uuid: this.reportUUID,
        userUUID: this.reportUUID
          ? this.record.details.user.uuid
          : this.createForm.user.uuid,
        description,
        type: this.createForm.type,
        platformType: this.createForm.platformType,
      };
      const result = await (this.reportUUID
        ? this.updateReport
        : this.createReport)({ data, filter: { ...this.searchCriteria } });
      if (result) this.$emit("done");
    },
  },
};
</script>
